import { useEffect, useState } from 'react';
import Layout from '../components/layout';
import Title from '../components/title';
import { supabase } from '../supabase';
import './tablePage.css';
import searchIcon from '../assets/search.svg';
import editIcon from '../assets/edit.svg';
import deleteIcon from '../assets/delete.svg';

const BOOTHS_PER_PAGE = 4;

const Booths = () => {
  const [booths, setBooths] = useState([]);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [name, setName] = useState('');
  const [location, setLocation] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [id, setId] = useState('');
  const [editName, setEditName] = useState('');
  const [editLocation, setEditLocation] = useState('');
  const [isEditOpen, setIsEditOpen] = useState(false);

  const getBooths = async () => {
    const res = await supabase.from('booths').select('*').order('id', { ascending: false });
    setBooths(res.data || []);
  };

  useEffect(() => {
    getBooths();
  }, []);

  const createBooth = async (e) => {
    e.preventDefault();
    await supabase.from('booths').insert([{ name_en: name, location }]);
    setName('');
    setLocation('');
    setIsCreateOpen(false);
    await getBooths();
  };

  const editBooth = (booth) => {
    setId(booth.id);
    setEditName(booth.name_en || '');
    setEditLocation(booth.location || '');
    setIsEditOpen(true);
  };

  const updateBooth = async (e) => {
    e.preventDefault();
    await supabase.from('booths').update({ name_en: editName, location: editLocation }).eq('id', id);
    setIsEditOpen(false);
    await getBooths();
  };

  const deleteBooth = async (boothId) => {
    await supabase.from('booths').delete().eq('id', boothId);
    await getBooths();
  };

  const filteredBooths = booths.filter((booth) => {
    const value = search.toLowerCase();
    return booth.id.toString().includes(value) || booth.name_en?.toLowerCase().includes(value) || booth.location?.toLowerCase().includes(value);
  });

  const totalPages = Math.max(1, Math.ceil(filteredBooths.length / BOOTHS_PER_PAGE));
  const startIndex = (currentPage - 1) * BOOTHS_PER_PAGE;
  const paginatedBooths = filteredBooths.slice(startIndex, startIndex + BOOTHS_PER_PAGE);

  return (
    <Layout>
      <div className="table_page">
        <Title subtitle="Manage booths" title="Booths" />
        <div className="table_controls_card">
          <label className="table_search" htmlFor="booths-search">
            <img alt="" src={searchIcon} />
            <input
              id="booths-search"
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search booths..."
              type="text"
              value={search}
            />
          </label>
          <div className="table_controls_actions">
            <span>{filteredBooths.length} total records</span>
            <button className="table_primary_button" onClick={() => setIsCreateOpen(true)} type="button">
              Add Booth
            </button>
          </div>
        </div>
        <div className="table_card">
          <div className="table_scroll">
            <table className="data_table">
              <thead><tr><th>ID</th><th>Name</th><th>Location</th><th>Actions</th></tr></thead>
              <tbody>
                {paginatedBooths.map((booth) => (
                  <tr key={booth.id}>
                    <td>{booth.id}</td>
                    <td>{booth.name_en}</td>
                    <td>{booth.location}</td>
                    <td>
                      <div className="table_actions">
                        <button
                          aria-label={`Edit booth #${booth.id}`}
                          className="table_icon_button"
                          onClick={() => editBooth(booth)}
                          type="button"
                        >
                          <img alt="" src={editIcon} />
                        </button>
                        <button
                          aria-label={`Delete booth #${booth.id}`}
                          className="table_icon_button table_icon_button_danger"
                          onClick={() => deleteBooth(booth.id)}
                          type="button"
                        >
                          <img alt="" src={deleteIcon} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="table_pagination">
            <button disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)} type="button">Previous</button>
            <span>Page {currentPage} of {totalPages}</span>
            <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)} type="button">Next</button>
          </div>
        </div>
        {isCreateOpen && (
          <div
            aria-labelledby="add-booth-heading"
            aria-modal="true"
            className="table_modal_overlay"
            onClick={() => setIsCreateOpen(false)}
            role="dialog"
          >
            <div className="table_modal" onClick={(e) => e.stopPropagation()}>
              <div className="table_modal_header">
                <div>
                  <h2 id="add-booth-heading">Add Booth</h2>
                </div>
                <button
                  aria-label="Close add booth form"
                  className="table_modal_close"
                  onClick={() => setIsCreateOpen(false)}
                  type="button"
                >
                  &#x2715;
                </button>
              </div>
              <form className="table_form" onSubmit={createBooth}>
                <div className="table_form_grid">
                  <label className="table_form_field">
                    <span>Name</span>
                    <input onChange={(e) => setName(e.target.value)} placeholder="Name" value={name} />
                  </label>
                  <label className="table_form_field">
                    <span>Location</span>
                    <input onChange={(e) => setLocation(e.target.value)} placeholder="Location" value={location} />
                  </label>
                </div>
                <div className="table_form_actions">
                  <button className="table_secondary_button" onClick={() => setIsCreateOpen(false)} type="button">
                    Cancel
                  </button>
                  <button className="table_primary_button" type="submit">Create</button>
                </div>
              </form>
            </div>
          </div>
        )}
        {isEditOpen && (
          <div
            aria-labelledby="edit-booth-heading"
            aria-modal="true"
            className="table_modal_overlay"
            onClick={() => setIsEditOpen(false)}
            role="dialog"
          >
            <div className="table_modal" onClick={(e) => e.stopPropagation()}>
              <div className="table_modal_header">
                <div>
                  <h2 id="edit-booth-heading">Edit Booth</h2>
                </div>
                <button
                  aria-label="Close edit booth form"
                  className="table_modal_close"
                  onClick={() => setIsEditOpen(false)}
                  type="button"
                >
                  &#x2715;
                </button>
              </div>
              <form className="table_form" onSubmit={updateBooth}>
                <div className="table_form_grid">
                  <label className="table_form_field">
                    <span>Name</span>
                    <input onChange={(e) => setEditName(e.target.value)} value={editName} />
                  </label>
                  <label className="table_form_field">
                    <span>Location</span>
                    <input onChange={(e) => setEditLocation(e.target.value)} value={editLocation} />
                  </label>
                </div>
                <div className="table_form_actions">
                  <button className="table_secondary_button" onClick={() => setIsEditOpen(false)} type="button">
                    Cancel
                  </button>
                  <button className="table_primary_button" type="submit">Save</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Booths;
